<template>
    <v-dialog
        v-model="customerSearch"
        fullscreen
        persistent
        hide-overlay
        transition="dialog-bottom-transition"
        :content-class="twilioChat ? 'twilioDrawer' : ''"
    >
        <template v-slot:activator="{ on }">
                  <v-btn
                      v-on="on"
                      data-testid="facility-tenant-find-btn"
                      class="facility-tenant-find-btn primary px-3 white--text"
                      outlined
                  >
                      <v-icon class="mr-3">search</v-icon>
                      <span>Find Tenant</span>
                  </v-btn>
        </template>

        <v-card v-if="customerSearch">
            <v-card-title class="text-h5" primary-title>
                Tenant Search
            </v-card-title>
            <v-divider />
            <v-card-text>
                <v-layout v-if="modalClosing" justify-center>
                    <v-progress-circular
                        :size="60"
                        :width="6"
                        indeterminate
                        class="my-3"
                    />
                </v-layout>
                <v-form v-else ref="searchForm">
                    <v-container>
                        <div class="subheading mb-3">You must fill out at least one field to submit a search request.</div>
                        <v-layout>
                            <app-input
                                data-testid="facility-tenant-first-name-input"
                                class="facility-tenant-first-name-input mr-2"
                                v-model="tenant.first_name"
                                filled
                                label="First Name"
                                :rules="{ required: false }"
                                @keyup="keyupSubmit"
                            >
                              First Name
                            </app-input>
                            <app-input
                                data-testid="facility-tenant-last-name-input"
                                class="facility-tenant-first-name-input mx-1"
                                v-model="tenant.last_name"
                                filled
                                label="Last Name"
                                :rules="{ required: false }"
                                @keyup="keyupSubmit"
                            >
                              Last Name
                            </app-input>
                            <app-input
                                data-testid="facility-tenant-phone-number-input"
                                class="facility-tenant-phone-number-input ml-2"
                                v-model="tenant.phone"
                                filled
                                label="Phone Number"
                                return-masked-value
                                mask="###-###-####"
                                :rules="{ required: false, phone: true }"
                                @keyup="keyupSubmit"
                            >
                              Phone Number
                            </app-input>
                        </v-layout>
                        <v-layout>
                            <app-input
                                data-testid="facility-tenant-email-input"
                                class="facility-tenant-email-input mr-2"
                                filled
                                label="Email"
                                v-model="tenant.email"
                                :rules="{ required: false, email: true }"
                                @keyup="keyupSubmit"
                            >
                              Email
                            </app-input>
                            <app-input
                                data-testid="facility-tenant-access-code-input"
                                class="facility-tenant-access-code-input mx-1"
                                filled
                                label="Access Code"
                                v-model="tenant.access_code"
                                :rules="{ required: false, numeric: true }"
                                @keyup="keyupSubmit"
                            >
                              Access Code
                            </app-input>
                        </v-layout>
                        <v-layout v-if="searchLoading" justify-center>
                            <v-progress-circular
                                :size="60"
                                :width="6"
                                indeterminate
                                class="my-3"
                            />
                        </v-layout>
                        <v-layout v-else column>
                            <v-layout column v-show="hasSearched">
                                <span class="subheading mb-2">Search results:</span>
                                <v-divider />
                                <template v-if="errorMessage">
                                  <div class="error pa-2">{{ errorMessage }}</div>
                                </template>
                                <v-data-table v-else
                                    :headers="headers"
                                    :items="searchResults"
                                    item-key="sUnitName"
                                    :loading="loading"
                                    no-data-text="No results found"
                                >
                                    <template slot="item" slot-scope="props">
                                      <tr>
                                        <td>{{ props.item.first_name }}</td>
                                        <td>{{ props.item.last_name }}</td>
                                        <td>{{ props.item.unit_numbers }}</td>
                                        <td>{{ props.item.is_active ? "Yes" : "No" }}</td>
                                        <td>{{ props.item.access_code }}</td>
                                        <td>{{ props.item.email }}</td>
                                        <td>{{ formatPhone(props.item.phone_number) }}</td>
                                        <td>
                                            <v-btn
                                                v-if="toTenantView"
                                                data-testid="facility-tenant-tenant-view-btn"
                                                class="facility-tenant-tenant-view-btn primary"
                                                small
                                                :to="{ name: 'tenantDetails', params: { tenantId: props.item.pms_tenant_id } }"
                                            >
                                              Select
                                            </v-btn>
                                            <v-btn
                                                v-else
                                                data-testid="facility-tenant-tenant-selected-btn"
                                                class="primary facility-tenant-tenant-selected-btn"
                                                small
                                                @click="tenantSelected(props.item)"
                                            >
                                              Select
                                            </v-btn>
                                        </td>
                                      </tr>
                                    </template>
                                </v-data-table>
                            </v-layout>
                        </v-layout>
                    </v-container>
                </v-form>
            </v-card-text>
            <v-divider />
            <v-card-actions>
                <v-layout>
                    <v-btn
                        data-testid="facility-tenant-cancel-btn"
                        class="facility-tenant-cancel-btn mt-2 ml-3"
                        color="error"
                        @click="closeModal"
                        large
                    >
                      Cancel
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        id="facility-tenant-search-btn"
                        class="facility-tenant-search-btn mt-2 mr-3"
                        :loading="loading"
                        :disabled="!validSearch"
                        color="primary"
                        @click="findTenant"
                        large
                    >
                        Search
                    </v-btn>
                </v-layout>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { facilityTenantSearchHeaders } from "@/helpers/facilities/facility-tenant-search-data-table-headers";
import { inputRules } from "@/helpers/form-rules";

export default {
  props: ["toTenantView"],

  data() {
    return {
      customerSearch: false,
      hasSearched: false,
      loading: false,
      modalClosing: false,
      searchLoading: false,
      inputRules: inputRules,
      errorMessage: null,
      tenant: {
        first_name: "",
        last_name: "",
        phone: "",
        email: "",
        access_code: "",
        unit_number: ""
      },
      searchResults: [],
      headers: facilityTenantSearchHeaders
    };
  },

  methods: {
    keyupSubmit(event) {
      if (event.which === 13) { // Enter key
        this.findTenant();
      }
    },
    findTenant() {
      this.errorMessage = null;
      this.searchLoading = true;
      let payload = {
        id: this.$route.params.facilityId,
        tenant: this.tenant
      };
      this.$store.dispatch("findTenant", payload)
        .then(resp => {
          this.searchResults = resp.data;
          this.searchLoading = false;
          this.hasSearched = true;
        })
        .catch(async err => {
          this.searchLoading = false;
          this.hasSearched = true;
          this.errorMessage = await this.$store.dispatch("handleError", err).then(res => res);
        });
    },

    tenantSelected(tenant) {
      this.modalClosing = true;
      this.$emit("tenantSelected", tenant);
      this.collapse();
    },

    closeModal() {
      this.modalClosing = true;
      this.collapse();
    },

    collapse() {
      this.searchResults = [];
      this.customerSearch = false;
      this.hasSearched = false;
      this.$refs.searchForm.reset();
      this.modalClosing = false;
    },

    formatPhone(phone) {
      if (phone && typeof phone.replace === "function") {
        return phone.replace(/\D/g, "");
      }
    }
  },

  computed: {
    validSearch() {
      return !!((
        this.tenant.first_name ||
                this.tenant.last_name ||
                this.tenant.phone ||
                this.tenant.email ||
                this.tenant.access_code
      ));
    },

    twilioChat() {
      return this.$store.state.application.twilioChat;
    }

  }

};
</script>
